import React, { useState, useEffect } from 'react';
import { bannersAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineX,
  HiOutlinePhotograph,
  HiOutlineEye,
  HiOutlineEyeOff,
} from 'react-icons/hi';

const AdminBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activePosition, setActivePosition] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    buttonText: '',
    buttonLink: '',
    image: '',
    position: 'home-top',
    isActive: true,
  });

  const positions = [
    { value: 'home-top', label: 'Home - Top' },
    { value: 'home-middle', label: 'Home - Middle' },
    { value: 'home-bottom', label: 'Home - Bottom' },
    { value: 'shop-sidebar', label: 'Shop - Sidebar' },
    { value: 'product-page', label: 'Product Page' },
    { value: 'category-page', label: 'Category Page' },
  ];

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await bannersAPI.getAll();
      setBanners(response.data.banners || response.data);
    } catch (error) {
      toast.error('Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  };

  const filteredBanners = activePosition === 'all' 
    ? banners 
    : banners.filter(b => b.position === activePosition);

  const openModal = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setFormData({
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        buttonText: banner.buttonText || '',
        buttonLink: banner.buttonLink || '',
        image: banner.image || '',
        position: banner.position || 'home-top',
        isActive: banner.isActive !== false,
      });
    } else {
      setEditingBanner(null);
      setFormData({
        title: '',
        subtitle: '',
        buttonText: '',
        buttonLink: '',
        image: '',
        position: activePosition !== 'all' ? activePosition : 'home-top',
        isActive: true,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBanner(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formDataUpload,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image: data.url }));
        toast.success('Image uploaded');
      } else {
        // Fallback: use file preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({ ...prev, image: reader.result }));
        };
        reader.readAsDataURL(file);
        toast.success('Image ready');
      }
    } catch (error) {
      // Fallback: use file preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(e.target.files[0]);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.image) {
      toast.error('Please provide a title and image');
      return;
    }

    try {
      if (editingBanner) {
        await bannersAPI.update(editingBanner._id, formData);
        toast.success('Banner updated');
      } else {
        await bannersAPI.create(formData);
        toast.success('Banner created');
      }
      closeModal();
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save banner');
    }
  };

  const handleDelete = async () => {
    try {
      await bannersAPI.delete(deletingId);
      toast.success('Banner deleted');
      setDeletingId(null);
      fetchBanners();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const toggleActive = async (id, isActive) => {
    try {
      await bannersAPI.update(id, { isActive: !isActive });
      toast.success(`Banner ${isActive ? 'hidden' : 'visible'}`);
      fetchBanners();
    } catch (error) {
      toast.error('Failed to update banner');
    }
  };

  const getPositionLabel = (value) => {
    return positions.find(p => p.value === value)?.label || value;
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Banners</h1>
          <p className="text-gray-500">Manage promotional banners across the site</p>
        </div>
        <button
          onClick={() => openModal()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-luxury-charcoal text-white rounded-lg hover:bg-gray-800"
        >
          <HiOutlinePlus className="w-5 h-5" />
          Add Banner
        </button>
      </div>

      {/* Position Tabs */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActivePosition('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activePosition === 'all'
                ? 'bg-luxury-charcoal text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All ({banners.length})
          </button>
          {positions.map((pos) => {
            const count = banners.filter(b => b.position === pos.value).length;
            return (
              <button
                key={pos.value}
                onClick={() => setActivePosition(pos.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activePosition === pos.value
                    ? 'bg-luxury-charcoal text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {pos.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Banners Grid */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : filteredBanners.length === 0 ? (
          <div className="text-center py-20">
            <HiOutlinePhotograph className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {activePosition === 'all' ? 'No banners yet' : `No banners for ${getPositionLabel(activePosition)}`}
            </p>
            <button 
              onClick={() => openModal()}
              className="mt-4 text-luxury-gold hover:underline"
            >
              Create a banner
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {filteredBanners.map((banner) => (
              <div 
                key={banner._id}
                className={`border rounded-lg overflow-hidden ${
                  banner.isActive ? 'border-gray-200' : 'border-gray-100 opacity-60'
                }`}
              >
                {/* Image */}
                <div className="aspect-video bg-gray-100 relative group">
                  {banner.image ? (
                    <img 
                      src={banner.image} 
                      alt={banner.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <HiOutlinePhotograph className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  
                  {/* Actions Overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => toggleActive(banner._id, banner.isActive)}
                      className="p-2 bg-white rounded-full text-gray-700 hover:text-gray-900"
                      title={banner.isActive ? 'Hide' : 'Show'}
                    >
                      {banner.isActive ? (
                        <HiOutlineEyeOff className="w-5 h-5" />
                      ) : (
                        <HiOutlineEye className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => openModal(banner)}
                      className="p-2 bg-white rounded-full text-gray-700 hover:text-gray-900"
                    >
                      <HiOutlinePencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setDeletingId(banner._id)}
                      className="p-2 bg-white rounded-full text-gray-700 hover:text-red-600"
                    >
                      <HiOutlineTrash className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold line-clamp-1">{banner.title}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${
                      banner.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {banner.isActive ? 'Active' : 'Hidden'}
                    </span>
                  </div>
                  
                  {banner.subtitle && (
                    <p className="text-gray-500 text-sm line-clamp-1 mb-2">{banner.subtitle}</p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded">
                      {getPositionLabel(banner.position)}
                    </span>
                    {banner.buttonText && (
                      <span className="text-xs text-gray-500">
                        "{banner.buttonText}"
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Banner Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-4">
          <div className="bg-white rounded-lg max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editingBanner ? 'Edit Banner' : 'Create Banner'}
              </h3>
              <button onClick={closeModal}>
                <HiOutlineX className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banner Image *
                </label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg">
                  {formData.image ? (
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100">
                      <img 
                        src={formData.image} 
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                        className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-lg"
                      >
                        <HiOutlineX className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center py-8 cursor-pointer">
                      <HiOutlinePhotograph className="w-10 h-10 text-gray-400 mb-2" />
                      <span className="text-gray-500">
                        {uploading ? 'Uploading...' : 'Click to upload'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                  placeholder="Summer Sale"
                />
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                  placeholder="Up to 50% off"
                />
              </div>

              {/* Position */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position
                </label>
                <select
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                >
                  {positions.map((pos) => (
                    <option key={pos.value} value={pos.value}>
                      {pos.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Button Text & Link */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={formData.buttonText}
                    onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                    placeholder="Shop Now"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button Link
                  </label>
                  <input
                    type="text"
                    value={formData.buttonLink}
                    onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                    placeholder="/shop/sale"
                  />
                </div>
              </div>

              {/* Active */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-luxury-gold"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Active (visible on site)
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-luxury-charcoal text-white rounded-lg hover:bg-gray-800"
                >
                  {editingBanner ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">Delete Banner</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this banner? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeletingId(null)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBanners;
